import { Badge, Button, cn } from '@workingclock/ui';
import { Download, KeyRound, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AppStoreButton } from '../components/common/app-store-button';
import { GooglePlayButton } from '../components/common/google-play-button';
import { DesktopFrame, MiniEntryList, MiniTimer } from '../components/home/device-frames';
import { Container } from '../components/layout/container';
import {
  desktopDownloads,
  desktopOses,
  useDetectedDesktopOs,
  useLatestDesktopVersion,
} from '../lib/desktop-downloads';
import { env } from '../lib/env';
import { usePageMeta } from '../lib/use-page-meta';

export function DownloadPage() {
  const { t } = useTranslation('marketing');
  // Both resolve after mount only, so the pre-rendered HTML is the neutral
  // "choose your platform" state and hydration never sees a mismatch.
  const detectedOs = useDetectedDesktopOs();
  const version = useLatestDesktopVersion();

  usePageMeta(t('download.meta.title'), t('download.meta.description'));

  const primary = detectedOs ? desktopDownloads[detectedOs][0] : null;

  return (
    <>
      <section className="border-b border-border py-16 md:py-24">
        <Container className="grid items-center gap-12 md:grid-cols-2">
          <div>
            <Badge variant="gold" className="mb-5">
              {t('download.hero.eyebrow')}
            </Badge>
            <h1 className="text-hero text-ink">{t('download.hero.heading')}</h1>
            <p className="mt-6 max-w-[60ch] text-body text-muted">{t('download.hero.body')}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {detectedOs && primary ? (
                <Button size="lg" asChild>
                  <a href={primary.href}>
                    <Download aria-hidden />
                    {t('download.hero.ctaFor', { os: t(`download.os.${detectedOs}.name`) })}
                  </a>
                </Button>
              ) : (
                <Button size="lg" asChild>
                  <a href="#platforms">
                    <Download aria-hidden />
                    {t('download.hero.ctaChoose')}
                  </a>
                </Button>
              )}
              {detectedOs ? (
                <Button size="lg" variant="secondary" asChild>
                  <a href="#platforms">{t('download.hero.ctaOtherPlatforms')}</a>
                </Button>
              ) : null}
            </div>
            <p className="mt-6 text-label text-muted">
              {version ? `${t('download.hero.version', { version })} · ` : null}
              {t('download.hero.note')}
            </p>
          </div>

          <DesktopFrame className="mx-auto w-full max-w-sm">
            <MiniTimer project={t('home.crossPlatform.demoProject')} time={t('home.crossPlatform.demoTime')} active />
            <div className="mt-4 border-t border-border pt-4">
              <MiniEntryList
                entries={[
                  { label: t('home.crossPlatform.entries.onboardingCall'), duration: '00:42:11' },
                  { label: t('home.crossPlatform.entries.followUpNotes'), duration: '00:11:03' },
                  { label: t('home.crossPlatform.entries.invoiceDraft'), duration: '00:26:40' },
                ]}
              />
            </div>
          </DesktopFrame>
        </Container>
      </section>

      <section id="platforms" className="scroll-mt-20 border-b border-border py-16 md:py-24">
        <Container>
          <h2 className="text-headline text-ink">{t('download.platforms.heading')}</h2>
          <p className="mt-2 max-w-2xl text-body text-muted">{t('download.platforms.body')}</p>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {desktopOses.map((os) => {
              const [first, ...rest] = desktopDownloads[os];
              const isDetected = os === detectedOs;
              return (
                <div
                  key={os}
                  className={cn(
                    'flex flex-col rounded-md border bg-bg p-6',
                    isDetected ? 'border-gold' : 'border-border',
                  )}
                >
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-title text-ink">{t(`download.os.${os}.name`)}</h3>
                    {isDetected ? <Badge variant="gold">{t('download.platforms.detected')}</Badge> : null}
                  </div>
                  <p className="mt-2 text-body text-muted">{t(`download.os.${os}.requirements`)}</p>

                  <div className="mt-6 flex flex-1 flex-col justify-end gap-3">
                    <Button variant={isDetected ? 'primary' : 'secondary'} asChild>
                      <a href={first.href}>
                        <Download aria-hidden />
                        {t(`download.formats.${first.format}`)}
                        <span className="font-mono text-label opacity-70">{first.extension}</span>
                      </a>
                    </Button>
                    {rest.map((file) => (
                      <a
                        key={file.format}
                        href={file.href}
                        className="text-label font-medium text-gold-deep underline-offset-4 hover:underline"
                      >
                        {t(`download.formats.${file.format}`)}{' '}
                        <span className="font-mono text-muted">{file.extension}</span>
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      <section className="border-b border-border bg-surface py-16 md:py-20">
        <Container className="grid gap-10 md:grid-cols-2">
          <div className="flex items-start gap-3">
            <RefreshCw className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden />
            <div>
              <h2 className="text-title text-ink">{t('download.updates.heading')}</h2>
              <p className="mt-2 max-w-[55ch] text-body text-muted">{t('download.updates.body')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <KeyRound className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden />
            <div>
              <h2 className="text-title text-ink">{t('download.signIn.heading')}</h2>
              <p className="mt-2 max-w-[55ch] text-body text-muted">
                {t('download.signIn.bodyPre')}{' '}
                <Link to="/help/desktop" className="text-gold-deep underline-offset-4 hover:underline">
                  {t('download.signIn.guideLink')}
                </Link>
                {t('download.signIn.bodyPost')}
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20">
        <Container>
          <h2 className="text-headline text-ink">{t('download.elsewhere.heading')}</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <div className="rounded-md border border-border bg-bg p-6">
              <h3 className="text-title text-ink">{t('download.elsewhere.mobile.heading')}</h3>
              <p className="mt-2 text-body text-muted">{t('download.elsewhere.mobile.body')}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <AppStoreButton />
                <GooglePlayButton />
              </div>
            </div>
            <div className="rounded-md border border-border bg-bg p-6">
              <h3 className="text-title text-ink">{t('download.elsewhere.web.heading')}</h3>
              <p className="mt-2 text-body text-muted">{t('download.elsewhere.web.body')}</p>
              <Button variant="secondary" asChild className="mt-5">
                <a href={env.appBaseUrl}>{t('download.elsewhere.web.cta')}</a>
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
